(function () {
  'use strict';

  // Render-job pages are bot pageviews. The Super App's preset render job loads
  // a configurator PDP with ?md_render_job=1 to capture a door image
  // (md-config-ui-image-export-manager.js renderConfigurationImage). Nothing the
  // theme controls may reach the dataLayer or store_visits from such a page, or
  // every render would count as a visit, an impression and a click.
  // Latched at load (nothing removes md_render_job from the URL); the export
  // manager's own latch is consulted too once it has loaded. This is an
  // anti-pollution guard, not access control.
  function renderJobFromUrl() {
    try {
      return (
        new URLSearchParams(window.location.search).get('md_render_job') === '1'
      );
    } catch (e) {
      return false;
    }
  }
  var RENDER_JOB = renderJobFromUrl();

  function isRenderJob() {
    if (RENDER_JOB) return true;
    try {
      var iem =
        window.MurphyDoorConfigurator &&
        window.MurphyDoorConfigurator.imageExportManager;
      return !!(
        iem &&
        typeof iem.isRenderJob === 'function' &&
        iem.isRenderJob() === true
      );
    } catch (e) {
      return false;
    }
  }

  // Internal (staff) traffic: snippets/md-internal-traffic.liquid sets this
  // inline in <head> for staff browsers, before this file loads. Same
  // treatment as a render job: nothing reaches the dataLayer or store_visits.
  function isInternalTraffic() {
    try {
      return window.MD_INTERNAL_TRAFFIC === true;
    } catch (e) {
      return false;
    }
  }

  function isSuppressed() {
    return isInternalTraffic() || isRenderJob();
  }

  // No listeners, no sentinels: the whole declarative bridge stays dark.
  if (RENDER_JOB || isInternalTraffic()) return;

  function push(data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  function safePublish(eventName, payload) {
    try {
      if (
        window.Shopify &&
        Shopify.analytics &&
        typeof Shopify.analytics.publish === 'function'
      ) {
        Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) {
      /* silent */
    }
  }

  function bodyCtx() {
    const bd = document.body.dataset;
    return {
      page_type: bd.pageType || '',
      template: bd.template || '',
      product_id: bd.productId || '',
      collection_id: bd.collectionId || '',
    };
  }

  // Safety net for phone taps ONLY.
  //
  // A phone tap is the single highest-value uninstrumented signal on this store: 59.8% of
  // revenue involves a pre-order phone call, and the order side of that bridge already
  // exists — the tap side is what was missing. Every tel: link in .liquid now carries an
  // explicit data-track-click, but four more live inside `sections/footer-group.json`
  // richtext settings, which are merchant-owned and re-serialized by the Theme Editor —
  // an attribute hand-added there is clobbered by the next admin edit. Rather than write
  // markup into a setting value that cannot hold it, catch those taps here.
  //
  // Deliberately narrow, on TWO independent axes. Both are load-bearing:
  //
  //   1. It fires only for an <a href="tel:..."> that has NO [data-track-click] ancestor,
  //      so it can never double-count an explicitly instrumented link.
  //   2. It fires only inside a container that DECLARES a [data-track-section] scope.
  //      Without (2) this is a page-wide net that depends on nothing but the *absence*
  //      of an attribute, so any future third-party app block, review widget, or blog
  //      rich-text rendering an untracked tel: anchor would silently start beaconing its
  //      visible text into store_visits. Requiring a declared scope makes "we only read
  //      from containers we marked ourselves" an invariant rather than a coincidence of
  //      today's markup, and it is what keeps the dynamic label below safe.
  //
  // 🚨 A scope marker must never wrap an `@app` block slot, or (2) is worthless: a
  // merchant can install an app and inject markup into that slot with no code review,
  // and it would land inside our declared trust boundary. `sections/footer.liquid` marks
  // each non-@app block individually for exactly this reason - do not "simplify" that by
  // hoisting the attribute up to the <footer> element, which is where it started.
  //
  // This is not a general auto-instrumentation layer, and must not grow into one. If you
  // ever need a new surface covered, add data-track-click there, or mark that container
  // with data-track-section deliberately.
  function emitUntrackedTelTap(target) {
    if (!target) return;
    const link = target.closest('a[href^="tel:"]');
    if (!link) return;
    const scope = link.closest('[data-track-section]');
    if (!scope) return;
    const payload = {
      element_action: 'call_expert',
      element_section: scope.dataset.trackSection || '',
      // aria-label first: assets/accessibility.js enhancePhoneLinks() rewrites the four
      // footer numbers to "Call phone number: <number>" at runtime (and strips title),
      // which usefully distinguishes the Ogden / West Haven / Lexington / support lines
      // from each other. These are Murphy Door's own published numbers, never customer
      // data, and the scope gate above bounds where this text can come from.
      element_label: (link.getAttribute('aria-label') || link.textContent || '')
        .trim()
        .slice(0, 80),
      element_position: '',
      ...bodyCtx(),
    };
    push({ event: 'element_click', ...payload });
    safePublish('md_element_click', payload);
  }

  document.addEventListener(
    'click',
    function (e) {
      // Skip synthetic/programmatic clicks (e.g. sticky bar calling nativeATC.click() or
      // apoBtn.click()). isTrusted=false for JS-dispatched events; true for real user actions
      // and keyboard-activated clicks (browser dispatches those as trusted).
      if (!e.isTrusted) return;
      if (isSuppressed()) return;
      // Firefox fires click events with e.target === Text node; Element.closest doesn't exist on Text.
      const target =
        e.target.nodeType === 1 ? e.target : e.target.parentElement;
      const el = target && target.closest('[data-track-click]');
      if (!el) {
        emitUntrackedTelTap(target);
        return;
      }
      const payload = {
        element_action: el.dataset.trackClick || '',
        element_section: el.dataset.trackSection || '',
        element_label: el.dataset.trackLabel || '',
        element_position: el.dataset.trackPosition || '',
        ...bodyCtx(),
      };
      push({ event: 'element_click', ...payload });
      safePublish('md_element_click', payload);
    },
    { passive: true }
  );

  // threshold:0 fires as soon as any pixel of the sentinel enters the viewport,
  // which is correct for zero-area sentinel divs used as scroll triggers.
  // var (not const/let): function-scoped so the pageshow bfcache handler below can close over it.
  var visObs = null;
  if ('IntersectionObserver' in window) {
    visObs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (isSuppressed()) return;
          const payload = {
            element_section: entry.target.dataset.trackVisible || '',
            element_label: entry.target.dataset.trackVisibleLabel || '',
            ...bodyCtx(),
          };
          push({ event: 'section_visible', ...payload });
          safePublish('md_section_visible', payload);
          visObs.unobserve(entry.target);
        });
      },
      { threshold: 0 }
    );

    document.querySelectorAll('[data-track-visible]').forEach(function (el) {
      visObs.observe(el);
    });
  }

  // Re-register sentinels after iOS Safari restores a page from bfcache.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && visObs) {
      document.querySelectorAll('[data-track-visible]').forEach(function (el) {
        // unobserve first so previously-fired sentinels re-fire on the restored page.
        visObs.unobserve(el);
        visObs.observe(el);
      });
    }
  });
})();
